import React, { useState } from 'react';
import PropTypes from 'prop-types';
// mui
import { Select, MenuItem, Box, Card, CardHeader, CardContent, FormControlLabel, Grid, Stack, Switch } from '@mui/material';
// components
import EmptyContent from 'src/components/EmptyContent';
import { isNullOrEmpty } from 'src/utils/checkEmpty';
import LabelWidget from 'src/components/_dashboard/common/LabelWidget';
import Gallery from './components/Automobile/Gallery';
import Table from './components/Automobile/Table';
import LCChart from '../common/LCChart';

import ComingSoonCover from 'src/components/ComingSoonCover';

// ----------------------------------------------------------------------

Automobile.propTypes = {
  mode: PropTypes.string,
  view: PropTypes.string,
  data: PropTypes.array
};

export const AU_TYPE = [
  { value: 'convertible', label: 'Convertible', type: 'Private Passenger' },
  { value: 'coupe', label: 'Coupe', type: 'Private Passenger' },
  { value: 'crossover', label: 'Crossover', type: 'Private Passenger' },
  { value: 'diesel', label: 'Diesel', type: 'Private Passenger' },
  { value: 'hybrid/electric', label: 'Hybrid/Electric', type: 'Private Passenger' },
  { value: 'luxury-car', label: 'Luxury Car', type: 'Private Passenger' },
  { value: 'suv', label: 'SUV', type: 'Private Passenger' },
  { value: 'sedan', label: 'Sedan', type: 'Private Passenger' },
  { value: 'sports-car', label: 'Sports Car', type: 'Private Passenger' },
  { value: 'truck', label: 'Truck', type: 'Private Passenger' },
  { value: 'van', label: 'Van', type: 'Private Passenger' },
  { value: 'wagon', label: 'Wagon', type: 'Private Passenger' },

  { value: 'atv', label: 'ATV', type: 'Motorcycle / Recreational' },
  { value: 'golf-cart', label: 'Golf Cart', type: 'Motorcycle / Recreational' },
  { value: 'motorcycle/moped', label: 'Motorcycle / Moped', type: 'Motorcycle / Recreational' },

  { value: 'custom', label: 'Custom', type: 'Specialty' }, // Added as per the category list
];


export default function Automobile({ mode, view = 'show', data = [], chartData = {} }) {
  const [viewAll, setViewAll] = useState(false);
  const [selectedChartIndex, setSelectedChartIndex] = useState(0); // 0: Purchase, 1: Market, 2: Estate

  const { vehicleTypeCount, vehicleTypeMarket, vehicleTypeRValue, vehicleTypeESValue } = chartData;
  const mergedVehicleTypes = AU_TYPE.map((type) => ({
    ...type,
    count: vehicleTypeCount[type.value] || 0,
    marketValue: vehicleTypeMarket[type.value] || 0,
    rValue: vehicleTypeRValue[type.value] || 0,
    esValue: vehicleTypeESValue[type.value] || 0,
  }));
  const calcStateExposure = () => {
    const statesSet = new Set();
    data.forEach((asset) => {
      if (asset?.regState) {
        statesSet.add(asset?.regState);
      }
    })
    return statesSet.size;
  }
  const chartTitles = ['Market Value', 'Replacement Value', 'Estate Value'];
  const typeCountMap = mergedVehicleTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.count;
    return acc;
  }, {});

  console.log(data);

  // Prepare chart arrays
  const labelCountList = Object.keys(typeCountMap); // e.g. ["Private Passenger", "Motorcycle / Recreational", "Specialty"]
  const dataCountList = Object.values(typeCountMap); // e.g. [totalCount1, totalCount2, totalCount3]
  const typeValueMap = mergedVehicleTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.marketValue;
    return acc;
  }, {});
  const typeRValueMap = mergedVehicleTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.rValue;
    return acc;
  }, {});
  const typeESValueMap = mergedVehicleTypes.reduce((acc, curr) => {
    if (!acc[curr.type]) {
      acc[curr.type] = 0;
    }
    acc[curr.type] += curr.esValue;
    return acc;
  }, {});
  const dataList = [
    Object.values(typeValueMap),
    Object.values(typeRValueMap),
    Object.values(typeESValueMap)];


  const getValidData = () => {
    return viewAll ? data : data.filter((item) => item.assetStatus === 'active');
  };

  // console.log(data);
  const renderData = () =>
    mode === 'gallery' ? (
      <Gallery data={data} />
    ) : (
      <Box sx={{ minHeight: 360 }}>
        <FormControlLabel
          sx={{ position: 'absolute', top: '46px', right: '46px', zIndex: 999 }}
          control={<Switch size="small" checked={viewAll} onChange={(event) => setViewAll(event.target.checked)} />}
          label={viewAll ? 'View Active' : 'View All'}
          labelPlacement="start"
        />
        <Table data={getValidData()} />
      </Box>
    );

  return (
    <Stack spacing={2}>
      {view === 'show' && (
        <Grid container>
          <Grid item xs={12} sm={6} md={4}>
            <Stack sx={{ m: 1, pb: 3, height: 420 }} spacing={1}  >
              <LabelWidget sx={{ height: '33%', justifyContent: 'space-between' }} label="Total number of vehicles" data={chartData.totalNumberOfVehicles} />
              <Grid container sx={{ height: '33%', justifyContent: 'space-between' }}>
                <LabelWidget
                  data={chartData.totalStatesWithVehicles}
                  label="Total States with Vehicles"
                  sx={{ height: '100%', width: '49%' }}
                />
                <LabelWidget
                  data={calcStateExposure()}
                  label="Total States Registered"
                  sx={{ height: '100%', width: '49%' }}
                />
              </Grid>
              <LabelWidget sx={{ height: '33%', justifyContent: 'space-between' }} label="Total number of drivers" data={chartData.totalNumberOfDrivers} />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ m: 1, display: 'flex', flexDirection: 'column', pb: 3, height: 420 }}>
              {isNullOrEmpty(dataCountList) && (
                <ComingSoonCover noData={true} style={{ marginTop: 25 }} />
              )}
              <CardHeader sx={{
                textAlign: 'center',
                '& .MuiCardHeader-content': {
                  width: '100%' // Make title span full width
                },
                '& .MuiCardHeader-action': {
                  position: 'absolute',
                  right: 12,
                  top: 12
                }
              }} title="Total Fleet Size" />
              <Box sx={{ flex: 1 }}>
                <LCChart
                  type="quantity"
                  label={labelCountList}
                  data={dataCountList}
                />
              </Box>
            </Card>
          </Grid>

          {/* ONE SWITCHABLE AUTOMOBILE VALUE CHART */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                m: 1,
                display: 'flex',
                flexDirection: 'column',
                pb: 3, // ✅ Adds bottom padding
                height: 420
              }}
            >
              <CardHeader
                sx={{
                  textAlign: 'center',
                  '& .MuiCardHeader-content': {
                    width: '100%' // Make title span full width
                  },
                  '& .MuiCardHeader-action': {
                    position: 'absolute',
                    right: 12,
                    top: 12
                  }
                }}
                title={
                  <Box sx={{ pt: 1 }}>Total Automobile Value</Box>
                }
                action={
                  <Select
                    size="small"
                    value={selectedChartIndex}
                    onChange={(e) => setSelectedChartIndex(Number(e.target.value))}
                    sx={{
                      minWidth: 120,
                      fontSize: 12,
                      '& .MuiSelect-select': {
                        py: 0.5,
                        px: 1.5
                      }
                    }}
                  >
                    {chartTitles.map((title, index) => (
                      <MenuItem key={title} value={index}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />

              <Box sx={{ flex: 1, pb: 2 }}> {/* ✅ Bottom padding inside chart box */}
                <LCChart
                  label={labelCountList}
                  data={dataList[selectedChartIndex]}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}
      <Card>
        <CardContent>{data.length > 0 ? renderData() : <EmptyContent />}</CardContent>
      </Card>
    </Stack>
  );
}
